import { useState, useEffect, useRef } from 'react';

interface UseTypewriterProps {
  text: string;
  maxDuration?: number;
  maxSpeed?: number;
  enabled?: boolean;
  onComplete?: () => void;
}

export const useTypewriter = ({ 
  text, 
  maxDuration = 3000,
  maxSpeed = 100,
  enabled = false,
  onComplete
}: UseTypewriterProps) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const indexRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!enabled) {
      setDisplayedText(text);
      setIsComplete(true);
      return;
    }

    setDisplayedText('');
    setIsComplete(false);
    indexRef.current = 0;

    const textLength = text.length;
    if (textLength === 0) {
      setIsComplete(true);
      return;
    }

    const idealSpeed = maxDuration / textLength;
    
    const minDelayBetweenChars = 1000 / maxSpeed;
    
    const calculatedSpeed = Math.max(idealSpeed, minDelayBetweenChars);

    const typeNextCharacter = () => {
      if (indexRef.current < text.length) {
        setDisplayedText(text.slice(0, indexRef.current + 1));
        indexRef.current++;
        timeoutRef.current = setTimeout(typeNextCharacter, calculatedSpeed);
      } else {
        setIsComplete(true);
      }
    };

    timeoutRef.current = setTimeout(typeNextCharacter, calculatedSpeed);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [text, maxDuration, maxSpeed, enabled]);

  // Llamar al callback cuando la animación se complete
  useEffect(() => {
    if (isComplete && onComplete) {
      onComplete();
    }
  }, [isComplete, onComplete]);

  return { displayedText, isComplete };
}; 